import React, { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Refresh, FileDownload, ArrowBack } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import { exportStyledExcel } from '../../../utils/excelExport';


const formatDate = (d) => {
  if (!d) return '-';
  const s = String(d).split('T')[0];
  const parts = s.split('-');
  if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return s;
};

function CustomerandItemWiseSaleReport() {
  const [dataItem, setDataItem]           = useState([]);
  const history = useHistory();
  const [fromDate, setFromDate]           = useState('');
  const [toDate, setToDate]               = useState('');
  const [reportType, setReportType]       = useState('1'); // 1=Customer Wise, 2=Item Wise
  const [isMobile, setIsMobile]           = useState(window.innerWidth <= 768);
  const [loading, setLoading]             = useState(false);
  const { companyId, branchId, token, username, companyName } = useAuth();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    setFromDate(`${y}-${m}-01`);
    setToDate(today.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (fromDate && toDate && companyId && branchId && token) {
      if (new Date(fromDate) > new Date(toDate)) {
        setDataItem([]);
        alert('From Date cannot be greater than To Date');
        return;
      }
      fetchTableData();
    }
  }, [fromDate, toDate, companyId, branchId, token, reportType]);

  const fetchTableData = async () => {
    if (new Date(fromDate) > new Date(toDate)) { alert('From Date cannot be greater than To Date'); return; }
    setDataItem([]);
    const t = setTimeout(() => setLoading(true), 300);
    try {
      const endpoint = reportType === '1'
        ? `/CustomerWiseSale/${companyId}/${branchId}/${fromDate}/${toDate}`
        : `/ItemWiseSale/${companyId}/${branchId}/${fromDate}/${toDate}`;
      const res = await fetchData('ReportApi', endpoint, token);
      const rows = res.response_data || res || [];
      setDataItem(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error('Error fetching sale report:', err);
    } finally {
      clearTimeout(t);
      setLoading(false);
    }
  };

  // Excel export — Customer Wise
  const handleExportCustomerWise = async () => {
    if (!dataItem.length) return;
    const colHeaders = ['Bill Date','Bill No','Item Name','Qty','Rate','Taxable','GST%','CGST','SGST','IGST','Total'];
    const rows = [];

    // Group by customer
    const grouped = dataItem.reduce((acc, r) => {
      if (!acc[r.MasterName]) acc[r.MasterName] = [];
      acc[r.MasterName].push(r);
      return acc;
    }, {});

    Object.entries(grouped).forEach(([customer, items]) => {
      // Customer header row
      rows.push([`Customer: ${customer}`, ...Array(colHeaders.length - 1).fill('')]);
      // Data rows
      items.forEach(r => {
        rows.push([formatDate(r.BillDate), r.BillNumber, r.ItemName, r.Quantity, r.Rate, r.TaxableAmount, `${r.GSTRate}%`, r.CGSTAmount, r.SGSTAmount, r.IGSTAmount, r.TotalAmount]);
      });
      // Subtotal row per customer
      const sQty   = items.reduce((s,r) => s + Number(r.Quantity||0), 0);
      const sTax   = items.reduce((s,r) => s + Number(r.TaxableAmount||0), 0);
      const sCGST  = items.reduce((s,r) => s + Number(r.CGSTAmount||0), 0);
      const sSGST  = items.reduce((s,r) => s + Number(r.SGSTAmount||0), 0);
      const sIGST  = items.reduce((s,r) => s + Number(r.IGSTAmount||0), 0);
      const sTotal = items.reduce((s,r) => s + Number(r.TotalAmount||0), 0);
      rows.push(['__SUBTOTAL__', '', 'Subtotal', sQty, '', sTax.toFixed(2), '', sCGST.toFixed(2), sSGST.toFixed(2), sIGST.toFixed(2), sTotal.toFixed(2)]);
    });

    const gQty   = dataItem.reduce((s,r) => s + Number(r.Quantity||0), 0);
    const gTax   = dataItem.reduce((s,r) => s + Number(r.TaxableAmount||0), 0);
    const gCGST  = dataItem.reduce((s,r) => s + Number(r.CGSTAmount||0), 0);
    const gSGST  = dataItem.reduce((s,r) => s + Number(r.SGSTAmount||0), 0);
    const gIGST  = dataItem.reduce((s,r) => s + Number(r.IGSTAmount||0), 0);
    const gTotal = dataItem.reduce((s,r) => s + Number(r.TotalAmount||0), 0);
    const totalsRow = ['','','Grand Total', gQty, '', gTax.toFixed(2), '', gCGST.toFixed(2), gSGST.toFixed(2), gIGST.toFixed(2), gTotal.toFixed(2)];

    await exportStyledExcel({
      reportTitle: 'CUSTOMER WISE SALE REPORT',
      sheetName: 'Customer Wise',
      fileName: `Sale_CustomerWise_${fromDate}_${toDate}.xlsx`,
      companyName, username, fromDate, toDate,
      headers: colHeaders,
      rows,
      totalsRow,
      columnWidths: [12,12,25,8,8,10,7,10,10,10,12],
    });
  };

  // Excel export — Item Wise
  const handleExportItemWise = async () => {
    if (!dataItem.length) return;
    const colHeaders = ['Item Name','Customer','Bill Date','Bill No','Qty','Rate','Taxable','GST%','CGST','SGST','IGST','Total'];
    const rows = [];

    // Group by item
    const grouped = dataItem.reduce((acc, r) => {
      if (!acc[r.ItemName]) acc[r.ItemName] = [];
      acc[r.ItemName].push(r);
      return acc;
    }, {});

    Object.entries(grouped).forEach(([itemName, items]) => {
      // Item header row
      rows.push([`Item: ${itemName}`, ...Array(colHeaders.length - 1).fill('')]);
      // Data rows
      items.forEach(r => {
        rows.push([r.ItemName, r.MasterName, formatDate(r.BillDate), r.BillNumber, r.Quantity, r.Rate, r.TaxableAmount, `${r.GSTRate}%`, r.CGSTAmount, r.SGSTAmount, r.IGSTAmount, r.TotalAmount]);
      });
      // Subtotal row per item
      const sQty   = items.reduce((s,r) => s + Number(r.Quantity||0), 0);
      const sTax   = items.reduce((s,r) => s + Number(r.TaxableAmount||0), 0);
      const sCGST  = items.reduce((s,r) => s + Number(r.CGSTAmount||0), 0);
      const sSGST  = items.reduce((s,r) => s + Number(r.SGSTAmount||0), 0);
      const sIGST  = items.reduce((s,r) => s + Number(r.IGSTAmount||0), 0);
      const sTotal = items.reduce((s,r) => s + Number(r.TotalAmount||0), 0);
      rows.push(['__SUBTOTAL__', '', '', 'Subtotal', sQty, '', sTax.toFixed(2), '', sCGST.toFixed(2), sSGST.toFixed(2), sIGST.toFixed(2), sTotal.toFixed(2)]);
    });

    const gQty   = dataItem.reduce((s,r) => s + Number(r.Quantity||0), 0);
    const gTax   = dataItem.reduce((s,r) => s + Number(r.TaxableAmount||0), 0);
    const gCGST  = dataItem.reduce((s,r) => s + Number(r.CGSTAmount||0), 0);
    const gSGST  = dataItem.reduce((s,r) => s + Number(r.SGSTAmount||0), 0);
    const gIGST  = dataItem.reduce((s,r) => s + Number(r.IGSTAmount||0), 0);
    const gTotal = dataItem.reduce((s,r) => s + Number(r.TotalAmount||0), 0);
    const totalsRow = ['','','','Grand Total', gQty, '', gTax.toFixed(2), '', gCGST.toFixed(2), gSGST.toFixed(2), gIGST.toFixed(2), gTotal.toFixed(2)];

    await exportStyledExcel({
      reportTitle: 'ITEM WISE SALE REPORT',
      sheetName: 'Item Wise',
      fileName: `Sale_ItemWise_${fromDate}_${toDate}.xlsx`,
      companyName, username, fromDate, toDate,
      headers: colHeaders,
      rows,
      totalsRow,
      columnWidths: [25,20,12,12,8,8,10,7,10,10,10,12],
    });
  };

  const thStyle = (align = 'left') => ({
    color: 'white', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase',
    letterSpacing: '0.2px', padding: '4px 6px', textAlign: align,
    background: '#43a047', height: '28px', whiteSpace: 'nowrap',
  });

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      {loading && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.9)', zIndex: 9999, backdropFilter: 'blur(4px)' }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} thickness={4} sx={{ color: '#1b5e20', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#1b5e20', fontWeight: 600, fontSize: '1rem' }}>Loading...</Typography>
          </Box>
        </Box>
      )}

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)', gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Customer &amp; Item Wise Sale Report</span>
          <div style={{ flex: 1, minWidth: 4 }} />
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <FormControl component="fieldset">
            <RadioGroup row value={reportType} onChange={e => setReportType(e.target.value)} style={{ gap: '8px', marginLeft: '4px' }}>
              <FormControlLabel value="1" control={<Radio size="small" sx={{ padding: '2px', '& .MuiSvgIcon-root': { fontSize: 15 }, color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: '#fff' } }} />} label={<span style={{ fontSize: '11px', color: '#fff' }}>Customer Wise</span>} />
              <FormControlLabel value="2" control={<Radio size="small" sx={{ padding: '2px', '& .MuiSvgIcon-root': { fontSize: 15 }, color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: '#fff' } }} />} label={<span style={{ fontSize: '11px', color: '#fff' }}>Item Wise</span>} />
            </RadioGroup>
          </FormControl>
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchTableData} disabled={loading} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={reportType === '1' ? handleExportCustomerWise : handleExportItemWise} disabled={!dataItem.length} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}>
              <FileDownload sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Scrollable Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 130px)' }}>

          {/* Customer Wise */}
          {reportType === '1' && (
            <TableContainer component={Paper} style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderRadius: '6px', overflow: 'auto', maxHeight: 'calc(100vh - 120px)' }}>
              <Table stickyHeader sx={{ minWidth: isMobile ? 900 : 650 }} size="small">
                <TableHead>
                  <TableRow>
                    {['Bill Date','Bill No','Item Name','Qty','Rate','Taxable','GST%','CGST','SGST','IGST','Total'].map(col => (
                      <TableCell key={col} style={thStyle(['Bill Date','Bill No','Item Name'].includes(col) ? 'left' : 'right')}>{col}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(
                    dataItem.reduce((acc, r) => { if (!acc[r.MasterName]) acc[r.MasterName] = []; acc[r.MasterName].push(r); return acc; }, {})
                  ).map(([customer, items], ci) => {
                    const sQty  = items.reduce((s,r) => s + Number(r.Quantity||0), 0);
                    const sTax  = items.reduce((s,r) => s + Number(r.TaxableAmount||0), 0);
                    const sCGST = items.reduce((s,r) => s + Number(r.CGSTAmount||0), 0);
                    const sSGST = items.reduce((s,r) => s + Number(r.SGSTAmount||0), 0);
                    const sIGST = items.reduce((s,r) => s + Number(r.IGSTAmount||0), 0);
                    const sTotal= items.reduce((s,r) => s + Number(r.TotalAmount||0), 0);
                    return (
                      <React.Fragment key={ci}>
                        <TableRow sx={{ backgroundColor: '#e0f2f1' }}>
                          <TableCell colSpan={11} sx={{ fontWeight: 'bold', fontSize: '12px', color: '#004d40', padding: '6px 8px' }}>{customer}</TableCell>
                        </TableRow>
                        {items.map((r, idx) => (
                          <TableRow key={idx} sx={{ '&:nth-of-type(even)': { backgroundColor: '#fafbfc' }, '&:hover': { backgroundColor: '#f1f5f9' }, borderBottom: '1px solid #f1f5f9', height: '28px' }}>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem' }}>{formatDate(r.BillDate)}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem' }}>{r.BillNumber}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem' }}>{r.ItemName}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem', textAlign: 'right' }}>{r.Quantity}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem', textAlign: 'right' }}>{Number(r.Rate).toFixed(2)}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem', textAlign: 'right' }}>{Number(r.TaxableAmount).toFixed(2)}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem', textAlign: 'right' }}>{r.GSTRate}%</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem', textAlign: 'right' }}>{Number(r.CGSTAmount).toFixed(2)}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem', textAlign: 'right' }}>{Number(r.SGSTAmount).toFixed(2)}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem', textAlign: 'right' }}>{Number(r.IGSTAmount).toFixed(2)}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem', textAlign: 'right', fontWeight: 700, color: '#1e40af' }}>{Number(r.TotalAmount).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ background: '#d2d2d7' }}>
                          <TableCell colSpan={3} sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px 6px', color: 'darkred', fontSize: '12px' }}>Subtotal</TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px 6px', color: 'darkred', fontSize: '12px' }}>{sQty}</TableCell>
                          <TableCell sx={{ padding: '3px 6px' }} />
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px 6px', color: 'darkred', fontSize: '12px' }}>{sTax.toFixed(2)}</TableCell>
                          <TableCell sx={{ padding: '3px 6px' }} />
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px 6px', color: 'darkred', fontSize: '12px' }}>{sCGST.toFixed(2)}</TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px 6px', color: 'darkred', fontSize: '12px' }}>{sSGST.toFixed(2)}</TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px 6px', color: 'darkred', fontSize: '12px' }}>{sIGST.toFixed(2)}</TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px 6px', color: 'darkred', fontSize: '12px' }}>{sTotal.toFixed(2)}</TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                  {dataItem.length > 0 && (() => {
                    const gQty  = dataItem.reduce((s,r) => s + Number(r.Quantity||0), 0);
                    const gTax  = dataItem.reduce((s,r) => s + Number(r.TaxableAmount||0), 0);
                    const gCGST = dataItem.reduce((s,r) => s + Number(r.CGSTAmount||0), 0);
                    const gSGST = dataItem.reduce((s,r) => s + Number(r.SGSTAmount||0), 0);
                    const gIGST = dataItem.reduce((s,r) => s + Number(r.IGSTAmount||0), 0);
                    const gTotal= dataItem.reduce((s,r) => s + Number(r.TotalAmount||0), 0);
                    return (
                      <TableRow sx={{ background: '#1b5e20', position: 'sticky', bottom: 0, zIndex: 1 }}>
                        <TableCell colSpan={3} sx={{ textAlign: 'right', fontWeight: 'bold', padding: '6px 8px', color: '#fff', fontSize: '12px' }}>Grand Total</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '6px 8px', color: '#fff', fontSize: '12px' }}>{gQty}</TableCell>
                        <TableCell sx={{ padding: '6px 8px' }} />
                        <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '6px 8px', color: '#fff', fontSize: '12px' }}>{gTax.toFixed(2)}</TableCell>
                        <TableCell sx={{ padding: '6px 8px' }} />
                        <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '6px 8px', color: '#fff', fontSize: '12px' }}>{gCGST.toFixed(2)}</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '6px 8px', color: '#fff', fontSize: '12px' }}>{gSGST.toFixed(2)}</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '6px 8px', color: '#fff', fontSize: '12px' }}>{gIGST.toFixed(2)}</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '6px 8px', color: '#fff', fontSize: '12px' }}>{gTotal.toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  })()}
                  {dataItem.length === 0 && !loading && (
                    <TableRow><TableCell colSpan={11} sx={{ textAlign: 'center', py: 3, color: '#9ca3af', fontSize: 13 }}>No data found</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Item Wise */}
          {reportType === '2' && (
            <TableContainer component={Paper} style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderRadius: '6px', overflow: 'auto', maxHeight: 'calc(100vh - 120px)' }}>
              <Table stickyHeader sx={{ minWidth: isMobile ? 900 : 650 }} size="small">
                <TableHead>
                  <TableRow>
                    {['Item Name','Customer','Bill Date','Bill No','Qty','Rate','Taxable','GST%','CGST','SGST','IGST','Total'].map(col => (
                      <TableCell key={col} style={thStyle(['Item Name','Customer','Bill Date','Bill No'].includes(col) ? 'left' : 'right')}>{col}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(
                    dataItem.reduce((acc, r) => { if (!acc[r.ItemName]) acc[r.ItemName] = []; acc[r.ItemName].push(r); return acc; }, {})
                  ).map(([itemName, customers], ii) => {
                    const sQty  = customers.reduce((s,r) => s + Number(r.Quantity||0), 0);
                    const sTax  = customers.reduce((s,r) => s + Number(r.TaxableAmount||0), 0);
                    const sCGST = customers.reduce((s,r) => s + Number(r.CGSTAmount||0), 0);
                    const sSGST = customers.reduce((s,r) => s + Number(r.SGSTAmount||0), 0);
                    const sIGST = customers.reduce((s,r) => s + Number(r.IGSTAmount||0), 0);
                    const sTotal= customers.reduce((s,r) => s + Number(r.TotalAmount||0), 0);
                    return (
                      <React.Fragment key={ii}>
                        <TableRow sx={{ backgroundColor: '#e0f2f1' }}>
                          <TableCell colSpan={12} sx={{ fontWeight: 'bold', fontSize: '12px', color: '#004d40', padding: '6px 8px' }}>{itemName}</TableCell>
                        </TableRow>
                        {customers.map((r, idx) => (
                          <TableRow key={idx} sx={{ '&:nth-of-type(even)': { backgroundColor: '#fafbfc' }, '&:hover': { backgroundColor: '#f1f5f9' }, borderBottom: '1px solid #f1f5f9', height: '28px' }}>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem' }}>{r.ItemName}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem' }}>{r.MasterName}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem' }}>{formatDate(r.BillDate)}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem' }}>{r.BillNumber}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem', textAlign: 'right' }}>{r.Quantity}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem', textAlign: 'right' }}>{Number(r.Rate).toFixed(2)}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem', textAlign: 'right' }}>{Number(r.TaxableAmount).toFixed(2)}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem', textAlign: 'right' }}>{r.GSTRate}%</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem', textAlign: 'right' }}>{Number(r.CGSTAmount).toFixed(2)}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem', textAlign: 'right' }}>{Number(r.SGSTAmount).toFixed(2)}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem', textAlign: 'right' }}>{Number(r.IGSTAmount).toFixed(2)}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 0.75, fontSize: '0.7rem', textAlign: 'right', fontWeight: 700, color: '#1e40af' }}>{Number(r.TotalAmount).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ background: '#eae5f3' }}>
                          <TableCell colSpan={4} sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px 6px', color: 'darkred', fontSize: '12px' }}>Subtotal</TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px 6px', color: 'darkred', fontSize: '12px' }}>{sQty}</TableCell>
                          <TableCell sx={{ padding: '3px 6px' }} />
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px 6px', color: 'darkred', fontSize: '12px' }}>{sTax.toFixed(2)}</TableCell>
                          <TableCell sx={{ padding: '3px 6px' }} />
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px 6px', color: 'darkred', fontSize: '12px' }}>{sCGST.toFixed(2)}</TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px 6px', color: 'darkred', fontSize: '12px' }}>{sSGST.toFixed(2)}</TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px 6px', color: 'darkred', fontSize: '12px' }}>{sIGST.toFixed(2)}</TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px 6px', color: 'darkred', fontSize: '12px' }}>{sTotal.toFixed(2)}</TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                  {dataItem.length > 0 && (() => {
                    const gQty  = dataItem.reduce((s,r) => s + Number(r.Quantity||0), 0);
                    const gTax  = dataItem.reduce((s,r) => s + Number(r.TaxableAmount||0), 0);
                    const gCGST = dataItem.reduce((s,r) => s + Number(r.CGSTAmount||0), 0);
                    const gSGST = dataItem.reduce((s,r) => s + Number(r.SGSTAmount||0), 0);
                    const gIGST = dataItem.reduce((s,r) => s + Number(r.IGSTAmount||0), 0);
                    const gTotal= dataItem.reduce((s,r) => s + Number(r.TotalAmount||0), 0);
                    return (
                      <TableRow sx={{ background: '#1b5e20', position: 'sticky', bottom: 0, zIndex: 1 }}>
                        <TableCell colSpan={4} sx={{ textAlign: 'right', fontWeight: 'bold', padding: '6px 8px', color: '#fff', fontSize: '12px' }}>Grand Total</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '6px 8px', color: '#fff', fontSize: '12px' }}>{gQty}</TableCell>
                        <TableCell sx={{ padding: '6px 8px' }} />
                        <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '6px 8px', color: '#fff', fontSize: '12px' }}>{gTax.toFixed(2)}</TableCell>
                        <TableCell sx={{ padding: '6px 8px' }} />
                        <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '6px 8px', color: '#fff', fontSize: '12px' }}>{gCGST.toFixed(2)}</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '6px 8px', color: '#fff', fontSize: '12px' }}>{gSGST.toFixed(2)}</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '6px 8px', color: '#fff', fontSize: '12px' }}>{gIGST.toFixed(2)}</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '6px 8px', color: '#fff', fontSize: '12px' }}>{gTotal.toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  })()}
                  {dataItem.length === 0 && !loading && (
                    <TableRow><TableCell colSpan={12} sx={{ textAlign: 'center', py: 3, color: '#9ca3af', fontSize: 13 }}>No data found</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

        </div>
      </div>
    </div>
  );
}

export default CustomerandItemWiseSaleReport;


